import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BackButton, HomeButton } from '../components/NavButtons';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { getSymbol } from '../utils/currency';
import { Star, Check, MapPin, CalendarBlank, Clock, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REVIEW_TAGS = [
  'Clean', 'Spacious', 'Great Value', 'Good Location', 'Friendly Staff',
  'Well Maintained', 'Quiet', 'Easy Access', 'On Time', 'Would Recommend'
];

const ReviewPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    fetchData();
  }, [bookingId]);

  const fetchData = async () => {
    try {
      const [bookingRes, reviewRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings`),
        axios.get(`${API_URL}/api/reviews/check/${bookingId}`),
      ]);
      const found = (bookingRes.data.bookings || []).find(b => b.booking_id === bookingId);
      setBooking(found || null);
      if (reviewRes.data.reviewed) {
        setExistingReview(reviewRes.data.review);
        setRating(reviewRes.data.review.rating);
        setReviewText(reviewRes.data.review.review_text || '');
        setSelectedTags(reviewRes.data.review.tags || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a star rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        booking_id: bookingId,
        listing_id: booking.listing_id,
        rating,
        review_text: reviewText || null,
        tags: selectedTags,
        photos: [],
      });
      toast.success('Review submitted! Thank you.');
      navigate('/bookings');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit review';
      toast.error(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">Booking not found</h2>
          <Button onClick={() => navigate('/bookings')} className="rounded-full">Go to Bookings</Button>
        </div>
      </div>
    );
  }

  const cs = getSymbol(booking.currency || booking.country);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
          <BackButton />
          <HomeButton />
          <h1 className="font-semibold text-slate-900 text-lg">{existingReview ? 'Your Review' : 'Write a Review'}</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Booking Summary Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100" data-testid="booking-summary">
          <div className="flex gap-4">
            {booking.listing_image ? (
              <img src={booking.listing_image} alt={booking.listing_title} className="w-20 h-20 rounded-xl object-cover" />
            ) : (
              <div className="w-20 h-20 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                <MapPin size={28} className="text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-slate-900 text-base truncate">{booking.listing_title}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                <MapPin size={13} /> {booking.listing_address}
              </p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CalendarBlank size={12} /> {booking.start_date}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {booking.start_time} - {booking.end_time}</span>
              </div>
              <p className="text-sm font-bold text-slate-900 mt-1">{cs}{booking.total_price}</p>
            </div>
          </div>
        </div>

        {existingReview ? (
          /* Already Reviewed - Read-only */
          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-5 border border-slate-100 text-center" data-testid="existing-review">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check size={28} className="text-emerald-600" weight="bold" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">You already reviewed this space</h3>
              <p className="text-sm text-slate-500 mb-4">Submitted on {new Date(existingReview.created_at).toLocaleDateString()}</p>
              <div className="flex justify-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} size={28} weight="fill" className={s <= existingReview.rating ? 'text-amber-400' : 'text-slate-200'} />
                ))}
              </div>
              {existingReview.review_text && (
                <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-4 text-left italic">"{existingReview.review_text}"</p>
              )}
              {existingReview.tags && existingReview.tags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                  {existingReview.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => navigate('/bookings')} className="w-full h-11 rounded-xl bg-blue-500 hover:bg-blue-600" data-testid="back-to-bookings-btn">
              Back to Bookings
            </Button>
          </div>
        ) : (
          /* Review Form */
          <div className="space-y-5">
            {/* Star Rating */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 text-center" data-testid="rating-section">
              <h3 className="text-base font-semibold text-slate-900 mb-1">How was your experience?</h3>
              <p className="text-sm text-slate-500 mb-4">Tap to rate</p>
              <div className="flex justify-center gap-2 mb-2">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s}
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-125 active:scale-95"
                    data-testid={`star-${s}`}>
                    <Star size={36} weight="fill"
                      className={s <= (hoverRating || rating) ? 'text-amber-400 drop-shadow-sm' : 'text-slate-200'} />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-sm font-medium text-amber-600" data-testid="rating-label">
                  {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
                </p>
              )}
            </div>

            {/* Tags */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="tags-section">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">What stood out? <span className="font-normal text-slate-400">(optional)</span></h3>
              <div className="flex flex-wrap gap-2">
                {REVIEW_TAGS.map(tag => {
                  const isOn = selectedTags.includes(tag);
                  return (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${isOn ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                      data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                      {isOn && <Check size={10} weight="bold" />}
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Text Review */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="text-section">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Share more details <span className="font-normal text-slate-400">(optional)</span></h3>
              <Textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Tell others about your experience — the space, the service, things you liked or could improve..."
                className="min-h-[100px] rounded-xl text-sm resize-none"
                maxLength={1000}
                data-testid="review-text-input"
              />
              <p className="text-xs text-slate-400 mt-1 text-right">{reviewText.length}/1000</p>
            </div>

            {/* Submit */}
            <Button onClick={handleSubmit} disabled={submitting || rating === 0}
              className="w-full h-12 rounded-xl bg-blue-500 hover:bg-blue-600 font-semibold text-base disabled:opacity-50 shadow-lg shadow-blue-500/20"
              data-testid="submit-review-btn">
              <PaperPlaneTilt size={18} className="mr-2" weight="fill" />
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewPage;
